import Emittery from 'emittery';

import { Waypoint } from 'model/Order';
import {
  QuoteRequest,
  QuoteResponse,
} from 'model/Quote';
import { Vehicle } from 'model/Vehicle';

import { StripeCustomer } from './bff';

export const pageTitle: Record<string, string> = {
  '/': 'Get Quote',
  '/quote': 'Review Quote',
  '/customer-and-vehicle': 'Customer and Vehicle Info',
  '/pickup': 'Pickup Info',
  '/delivery': 'Delivery Info',
  '/payment': 'Payment Info',
  '/order-success': 'Order Success',
  '/payment-success': 'Order Success',
  '/payment-failure': 'Payment Failure',
  '/order-failure': 'Order Failure',
};

export enum SessionEventEnum {
    WIDTH = 'WIDTH',
    HEIGHT = 'HEIGHT',
    QUOTE_REQUESTED = 'QUOTE_REQUESTED',
    QUOTE_RECEIVED = 'QUOTE_RECEIVED',
    QUOTE_ACCEPTED = 'QUOTE_ACCEPTED',
    QUOTE_REJECTED = 'QUOTE_REJECTED',
    CUSTOMER_AND_VEHICLE_INFO = 'CUSTOMER_AND_VEHICLE_INFO',
    PICKUP_INFO = 'PICKUP_INFO',
    DELIVERY_INFO = 'DELIVERY_INFO',
    PAYMENT_SUBMITTED = 'PAYMENT_SUBMITTED',
    PAYMENT_SUCCESS = 'PAYMENT_SUCCESS',
    PAYMENT_FAILURE = 'PAYMENT_FAILURE',
    ORDER_SUBMITTED = 'ORDER_SUBMITTED',
    ORDER_SUCCESS = 'ORDER_SUCCESS',
    ORDER_FAILURE = 'ORDER_FAILURE'
}

export const SESSION_SCREEN: Record<keyof typeof SessionScreenEnum, string> = {
  REQUEST_QUOTE: '/',
  REVIEW_QUOTE: '/quote',
  CUSTOMER_AND_VEHICLE_INFO: '/customer-and-vehicle',
  PICKUP_INFO: '/pickup',
  DELIVERY_INFO: '/delivery',
  PAYMENT_INFO: '/payment',
  ORDER_SUCCESS: '/order-success',
  PAYMENT_SUCCESS: '/payment-success',
  PAYMENT_FAILURE: '/payment-failure',
  ORDER_FAILURE: '/order-failure',
};

export enum SessionScreenEnum {
    REQUEST_QUOTE = 'REQUEST_QUOTE',
    REVIEW_QUOTE = 'REVIEW_QUOTE',
    CUSTOMER_AND_VEHICLE_INFO = 'CUSTOMER_AND_VEHICLE_INFO',
    PICKUP_INFO = 'PICKUP_INFO',
    DELIVERY_INFO = 'DELIVERY_INFO',
    PAYMENT_INFO = 'PAYMENT_INFO',
    ORDER_SUCCESS = 'ORDER_SUCCESS',
    PAYMENT_SUCCESS = 'PAYMENT_SUCCESS',
    PAYMENT_FAILURE = 'PAYMENT_FAILURE',
    ORDER_FAILURE = 'ORDER_FAILURE'
}

interface SessionEvent<T> {
    type: keyof typeof SessionEventEnum;
    message: T;
}

interface PaymentEvents {
  submitPayment: undefined;
  addressCompleted: boolean;
  paymentCompleted: boolean;
  paymentError: string | undefined;
  termsAccepted: boolean;
}

export const emitter = new Emittery<PaymentEvents>();

const createEventClient = () => {
  const emitSessionEvent = <T = never, X = T>(event: SessionEvent<T>, xfmr?: (event: T) => X): void => {
    const xfmdEvent: SessionEvent<T | X> = {
      type: event.type,
      message: xfmr ? xfmr(event.message) : event.message,
    };
    if (event.type !== SessionEventEnum.HEIGHT) {
      gtag('event', event.type.toLowerCase());
    }
    window.parent.postMessage(xfmdEvent, { targetOrigin: '*' });
  };

  const emitQuoteRequested = (message: QuoteRequest) => emitSessionEvent({
    type: SessionEventEnum.QUOTE_REQUESTED,
    message,
  });

  const emitQuoteReceived = (message: QuoteResponse) => emitSessionEvent({
    type: SessionEventEnum.QUOTE_RECEIVED,
    message,
  });

  const emitQuoteAccepted = () => emitSessionEvent({
    type: SessionEventEnum.QUOTE_ACCEPTED,
    message: undefined,
  });

  const emitQuoteRejected = () => emitSessionEvent({
    type: SessionEventEnum.QUOTE_REJECTED,
    message: undefined,
  });

  const emitCustomerAndVehicleInfo = (customer: StripeCustomer, vehicles: Vehicle[]) => emitSessionEvent({
    type: SessionEventEnum.CUSTOMER_AND_VEHICLE_INFO,
    message: {
      customer,
      vehicles,
    },
  });

    interface FlatWaypoint extends Omit<Waypoint, 'date'> {
        date?: string;
    }

    const waypointFlattener = (waypoint: Waypoint): FlatWaypoint => {
      const { date, ...rest } = waypoint;
      return {
        ...rest,
        date: date ? `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}` : undefined,
      };
    };

    const emitPickupInfo = (pickup: Waypoint) => emitSessionEvent({
      type: SessionEventEnum.PICKUP_INFO,
      message: pickup,
    }, waypointFlattener);

    const emitDeliveryInfo = (delivery: Waypoint) => emitSessionEvent({
      type: SessionEventEnum.DELIVERY_INFO,
      message: delivery,
    }, waypointFlattener);

    const emitHeight = () => emitSessionEvent({
      type: SessionEventEnum.HEIGHT,
      message: document.documentElement.offsetHeight,
    });

    let lastHeight = 0;

    const updateHost = () => {
      if (document.documentElement.offsetHeight === lastHeight) {
        return;
      }
      lastHeight = document.documentElement.offsetHeight;
      emitHeight();
    };

    const emitPaymentSubmitted = () => emitSessionEvent({
      type: SessionEventEnum.PAYMENT_SUBMITTED,
      message: undefined,
    });

    const emitPaymentFailure = () => emitSessionEvent({
      type: SessionEventEnum.PAYMENT_FAILURE,
      message: undefined,
    });

    const emitOrderSubmitted = () => emitSessionEvent({
      type: SessionEventEnum.ORDER_SUBMITTED,
      message: undefined,
    });

    const emitOrderSuccess = () => {
      emitSessionEvent({
        type: SessionEventEnum.ORDER_SUCCESS,
        message: undefined,
      });
      emitSessionEvent({
        type: SessionEventEnum.PAYMENT_SUCCESS,
        message: undefined,
      });
    };

    const emitOrderFailure = () => emitSessionEvent({
      type: SessionEventEnum.ORDER_FAILURE,
      message: undefined,
    });

    interface WidthEvent extends SessionEvent<number> {
        type: SessionEventEnum.WIDTH,
        message: number;
    }

    const isWidthEvent = (event: SessionEvent<unknown>): event is WidthEvent => {
      return event.type === SessionEventEnum.WIDTH && typeof event.message === 'number';
    };

    let lastWidth = 0;

    const handleSessionMessage = <E extends SessionEvent<T>, T>(messageEvent: MessageEvent<E>) => {
      const event = messageEvent.data;
      if (isWidthEvent(event as WidthEvent)) {
        const nextWidth = event.message as number;
        if (nextWidth === lastWidth) {
          return;
        }
        lastWidth = nextWidth;
        emitHeight();
      }
    };

    const events = {
      updateHost,
      handleSessionMessage,
      emitQuoteRequested,
      emitQuoteReceived,
      emitQuoteAccepted,
      emitQuoteRejected,
      emitCustomerAndVehicleInfo,
      emitPickupInfo,
      emitDeliveryInfo,
      emitPaymentSubmitted,
      emitOrderSubmitted,
      emitPaymentFailure,
      emitOrderSuccess,
      emitOrderFailure,
    };

    return { events };
};

const eventClient = createEventClient();

export const { events } = eventClient;
